import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useQuery,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { getChats, getChatMessages } from "@/api/chat";
import { renderMarkdown } from "@/utils/markdown";
import { useAuthStore } from "@/stores/auth";
import { useChatStore } from "../stores/chat";
import type {
  ChatConversation,
  ChatMessage,
  FileAttachment,
  MessageSummary,
  ConfirmResponse,
  ChartSpec,
} from "@/types";

const PAGE_LIMIT = 20;

export function useChats() {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);

  const infiniteQuery = useInfiniteQuery({
    queryKey: ["chats", user?.userId],
    queryFn: ({ pageParam = 0 }) =>
      getChats(user!.userId, token!, PAGE_LIMIT, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((sum, p) => sum + p.chats.length, 0);
      return lastPage.chats.length > 0 && totalFetched < lastPage.total
        ? totalFetched
        : undefined;
    },
    enabled: !!user?.userId && !!token,
    initialPageParam: 0,
  });

  const chats: ChatConversation[] = [
    ...new Map(
      (infiniteQuery.data?.pages.flatMap((p) => p.chats) ?? []).map((chat) => [
        chat.id,
        chat,
      ]),
    ).values(),
  ];
  const total = infiniteQuery.data?.pages[0]?.total ?? 0;

  return {
    chats,
    total,
    loading: infiniteQuery.isPending,
    loadingMore: infiniteQuery.isFetchingNextPage,
    error: infiniteQuery.error as Error | null,
    hasMore: infiniteQuery.hasNextPage ?? false,
    loadMore: () => infiniteQuery.fetchNextPage({ cancelRefetch: false }),
    refreshChats: () => infiniteQuery.refetch(),
  };
}

type RawMessage = {
  id: number;
  by: string;
  data: string;
  is_doc?: boolean;
  metadata?: {
    type?: string;
    reviewMessageId?: number;
    files?: FileAttachment[];
    reviewBatch?: { batchId: string; recordCount: number };
    summary?: MessageSummary;
    charts?: ChartSpec[];
    result?: ConfirmResponse;
  };
};

function getPendingEmission(
  message: RawMessage,
  chatId: string,
  messagesById: Map<number, RawMessage>,
): ChatMessage["pendingEmission"] {
  const proposal =
    message.metadata?.type === "review_reminder"
      ? messagesById.get(message.metadata.reviewMessageId ?? 0)
      : message;
  const status = proposal?.metadata?.type;
  if (!proposal || (status !== "pending_create" && status !== "claiming")) {
    return undefined;
  }
  // Reminders refer to the original proposal, never their own message ID.
  return { chatId, messageId: proposal.id, status };
}

function getMessageSummary(message: RawMessage): MessageSummary | undefined {
  const result = message.metadata?.result;
  if (result) {
    return {
      created: result.created,
      failed: result.failed,
      remaining: result.remaining,
      uncertain: result.uncertain,
      errors: result.errors,
      byBusinessUnit: result.summary.byBusinessUnit,
      dateRange: result.summary.dateRange,
    };
  }
  return message.metadata?.summary;
}

interface ChatMessagesResult {
  rawMessages: RawMessage[];
  followUpQuestions: string[];
}

async function fetchRawMessages(
  chatId: string,
  token: string,
): Promise<ChatMessagesResult> {
  const data = await getChatMessages(chatId, token);
  if (!data) return { rawMessages: [], followUpQuestions: [] };
  return {
    rawMessages: (data.messages || []) as RawMessage[],
    followUpQuestions: data.follow_up_questions ?? [],
  };
}

export function useChatMessages() {
  const token = useAuthStore((s) => s.token);
  const userId = useAuthStore((s) => s.user?.userId);
  const queryClient = useQueryClient();
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const latestChatRef = useRef<string | null>(null);
  const requestRef = useRef(0);

  useEffect(
    () => () => {
      requestRef.current += 1;
      latestChatRef.current = null;
    },
    [token, userId],
  );

  const [prevToken, setPrevToken] = useState<string | null>(token);
  if (token !== prevToken) {
    setPrevToken(token);
    if (!token) {
      setActiveChatId(null);
    }
  }

  useEffect(() => {
    if (!token) {
      queryClient.removeQueries({ queryKey: ["chat-messages"] });
    }
  }, [token, queryClient]);

  const { data } = useQuery({
    queryKey: ["chat-messages", userId, activeChatId],
    queryFn: () => fetchRawMessages(activeChatId!, token!),
    enabled: !!activeChatId && !!token,
  });

  const messages: ChatMessage[] = useMemo(() => {
    const rawMessages = data?.rawMessages ?? [];
    const messagesById = new Map(
      rawMessages.map((message) => [message.id, message]),
    );
    return rawMessages.map((m) => ({
      role: m.by === "bot" ? ("assistant" as const) : ("user" as const),
      content: m.data,
      isReport: !!m.is_doc,
      fileAttachments:
        m.metadata?.type === "file" ? m.metadata?.files : undefined,
      reviewBatch: m.metadata?.reviewBatch,
      pendingEmission: activeChatId
        ? getPendingEmission(m, activeChatId, messagesById)
        : undefined,
      summary: getMessageSummary(m),
      charts: m.metadata?.charts,
    }));
  }, [data, activeChatId]);

  const loadChatMessages = useCallback(
    async (chatId: string): Promise<string[] | undefined> => {
      if (!token) return;
      const request = ++requestRef.current;
      const generation = useChatStore.getState().chatGeneration;
      const isCurrent = () =>
        request === requestRef.current &&
        generation === useChatStore.getState().chatGeneration &&
        latestChatRef.current === chatId;
      latestChatRef.current = chatId;
      try {
        const result = await queryClient.fetchQuery<ChatMessagesResult>({
          queryKey: ["chat-messages", userId, chatId],
          queryFn: () => fetchRawMessages(chatId, token),
          staleTime: 0,
        });
        if (!isCurrent()) return undefined;
        setActiveChatId(chatId);
        const messagesById = new Map(
          result.rawMessages.map((message) => [message.id, message]),
        );
        const chatMessages = await Promise.all(
          result.rawMessages.map(async (m) => {
            const role =
              m.by === "bot" ? ("assistant" as const) : ("user" as const);
            const rawContent = m.data;
            const content =
              role === "assistant"
                ? await renderMarkdown(rawContent)
                : rawContent;
            return {
              role,
              content,
              rawContent: role === "assistant" ? rawContent : undefined,
              isReport: !!m.is_doc,
              fileAttachments:
                m.metadata?.type === "file" ? m.metadata?.files : undefined,
              reviewBatch: m.metadata?.reviewBatch,
              pendingEmission: getPendingEmission(m, chatId, messagesById),
              summary: getMessageSummary(m),
              charts: m.metadata?.charts,
            };
          }),
        );
        if (!isCurrent()) return undefined;
        useChatStore.getState().setMessages(chatMessages);
        return result.followUpQuestions;
      } catch {
        if (isCurrent()) setActiveChatId(null);
        return undefined;
      }
    },
    [token, userId, queryClient],
  );

  const clearMessages = useCallback(() => {
    requestRef.current += 1;
    latestChatRef.current = null;
    setActiveChatId(null);
  }, []);

  return { messages, loadChatMessages, clearMessages };
}
